import React, { useState, useEffect, useCallback } from "react";
import { getRequest, putRequest, postRequest } from "../../utils/httpRequest";
import { colors, pageHeaderStyles } from "../AgenticPaymentAirdrop/styles";

const HEDERA_RE = /^0\.0\.\d+$/;
const POLYGON_RE = /^0x[0-9a-fA-F]{40}$/;

const card = {
  background: colors.white, border: `1px solid ${colors.border}`,
  borderRadius: "12px", padding: "20px", marginBottom: "20px",
};
const label = { display: "block", fontSize: "13px", fontWeight: 600, color: "#555", marginBottom: "6px" };
const input = {
  width: "100%", padding: "10px 12px", border: "1px solid #ccc", borderRadius: "6px",
  fontSize: "14px", boxSizing: "border-box", fontFamily: "monospace",
};
const btn = (bg, disabled) => ({
  display: "inline-flex", alignItems: "center", justifyContent: "center", minHeight: "38px",
  background: bg, color: "#fff", border: "none", borderRadius: "6px", padding: "9px 18px",
  fontSize: "13px", fontWeight: 700, cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.6 : 1,
});
const notice = (ok) => ({
  background: ok ? "#d4edda" : "#f8d7da", color: ok ? "#155724" : "#721c24",
  border: `1px solid ${ok ? "#c3e6cb" : "#f5c6cb"}`, borderRadius: "6px",
  padding: "10px 12px", fontSize: "13px", marginTop: "12px",
});

const GenesisSettings = ({ pageUrl = "/admin/genesis_settings" }) => {
  const url = (action) => `${pageUrl.replace(/\/$/, "")}/${action}`;

  // ── Reserved names ──
  const [names, setNames] = useState([]);
  const [assignments, setAssignments] = useState({}); // { [name]: { assigned, assignedTo } }
  const [nameFilter, setNameFilter] = useState("");
  const [newName, setNewName] = useState("");
  const [savingNames, setSavingNames] = useState(false);
  const [namesMsg, setNamesMsg] = useState(null); // { ok, text }

  // ── USDC receivers ──
  const [receivers, setReceivers] = useState({});
  const [defaults, setDefaults] = useState({});
  const [hedera, setHedera] = useState("");
  const [polygon, setPolygon] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [savingRecv, setSavingRecv] = useState(false);
  const [recvMsg, setRecvMsg] = useState(null);
  const [revertField, setRevertField] = useState(null); // 'hedera' | 'polygon'
  const [revertingField, setRevertingField] = useState(false);

  // "Choose from existing account" picker
  const [pickerFor, setPickerFor] = useState(null); // 'hedera' | 'polygon'
  const [pickerQuery, setPickerQuery] = useState("");
  const [pickerRows, setPickerRows] = useState([]);
  const [pickerLoading, setPickerLoading] = useState(false);

  // ── Assign reserved name to a user ──
  const [assignName, setAssignName] = useState("");
  const [assignReason, setAssignReason] = useState("");
  const [userQuery, setUserQuery] = useState("");
  const [userResults, setUserResults] = useState([]);
  const [userLoading, setUserLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [assigning, setAssigning] = useState(false);
  const [assignMsg, setAssignMsg] = useState(null); // { ok, text }

  const loadNames = useCallback(async () => {
    try {
      const res = await getRequest(url("reserved_names"));
      if (res && res.success) {
        setNames((res.data && res.data.names) || []);
        setAssignments((res.data && res.data.assignments) || {});
      }
    } catch (_e) { /* leave empty */ }
  }, [pageUrl]);

  const loadReceivers = useCallback(async () => {
    try {
      const res = await getRequest(url("usdc_receivers"));
      if (res && res.success) {
        setReceivers((res.data && res.data.receivers) || {});
        setDefaults((res.data && res.data.defaults) || {});
      }
    } catch (_e) { /* leave empty */ }
  }, [pageUrl]);

  useEffect(() => { loadNames(); loadReceivers(); }, [loadNames, loadReceivers]);

  const addName = () => {
    const n = newName.trim();
    if (!n) return;
    if (names.some((x) => x.toLowerCase() === n.toLowerCase())) { setNewName(""); return; }
    setNames([...names, n]);
    setNewName("");
  };
  const removeName = (n) => setNames(names.filter((x) => x !== n));

  const saveNames = async () => {
    setSavingNames(true);
    setNamesMsg(null);
    try {
      const res = await putRequest(url("update_reserved_names"), { names });
      if (res && res.success) {
        setNames((res.data && res.data.names) || names);
        setNamesMsg({ ok: true, text: "Reserved names saved." });
      } else {
        setNamesMsg({ ok: false, text: (res && res.error) || "Save failed." });
      }
    } catch (err) {
      setNamesMsg({ ok: false, text: err?.data?.error || "Save failed. Please try again." });
    } finally {
      setSavingNames(false);
    }
  };

  // Double-assignment guard: if the name being assigned is already assigned to
  // someone (per the reserved-names assignment map), block the submit.
  const assignTarget = assignments[assignName.trim()];
  const assignConflict = !!(assignTarget && assignTarget.assigned);

  const hederaErr = hedera.trim() && !HEDERA_RE.test(hedera.trim());
  const polygonErr = polygon.trim() && !POLYGON_RE.test(polygon.trim());
  const nothingToSave = !hedera.trim() && !polygon.trim();

  const saveReceivers = async () => {
    setSavingRecv(true);
    setRecvMsg(null);
    const body = {};
    if (hedera.trim()) body.hedera = hedera.trim();
    if (polygon.trim()) body.polygon = polygon.trim();
    try {
      const res = await putRequest(url("update_usdc_receivers"), body);
      if (res && res.success) {
        setReceivers((res.data && res.data.receivers) || receivers);
        setHedera(""); setPolygon(""); setConfirmOpen(false);
        setRecvMsg({ ok: true, text: "USDC receiver wallet(s) updated." });
      } else {
        setRecvMsg({ ok: false, text: (res && res.error) || "Update failed." });
      }
    } catch (err) {
      setRecvMsg({ ok: false, text: err?.data?.error || "Update failed. Please try again." });
    } finally {
      setSavingRecv(false);
    }
  };

  // Load address options for the open picker (chain from the target field).
  const loadPicker = useCallback(async (field, q) => {
    const chain = field === "hedera" ? "HBAR" : "POL";
    setPickerLoading(true);
    try {
      const res = await getRequest(`${url("account_addresses")}?chain=${chain}&q=${encodeURIComponent(q || "")}`);
      setPickerRows((res && res.success && res.data && res.data.rows) || []);
    } catch (_e) {
      setPickerRows([]);
    } finally {
      setPickerLoading(false);
    }
  }, [pageUrl]);

  const openPicker = (field) => {
    setPickerFor(field);
    setPickerQuery("");
    setPickerRows([]);
    loadPicker(field, "");
  };

  // Debounce the picker search.
  useEffect(() => {
    if (!pickerFor) return undefined;
    const h = setTimeout(() => loadPicker(pickerFor, pickerQuery), 300);
    return () => clearTimeout(h);
  }, [pickerQuery, pickerFor, loadPicker]);

  const pickAddress = (address) => {
    if (pickerFor === "hedera") setHedera(address);
    if (pickerFor === "polygon") setPolygon(address);
    setPickerFor(null);
    setPickerQuery("");
  };

  const submitRevert = async () => {
    const field = revertField;
    if (!field) return;
    setRevertingField(true);
    setRecvMsg(null);
    try {
      const res = await putRequest(url("update_usdc_receivers"), { [field]: null });
      if (res && res.success) {
        setReceivers((res.data && res.data.receivers) || {});
        setRevertField(null);
        setRecvMsg({ ok: true, text: `${field} reverted to env default.` });
      } else {
        setRecvMsg({ ok: false, text: (res && res.error) || "Revert failed." });
      }
    } catch (err) {
      setRecvMsg({ ok: false, text: err?.data?.error || "Revert failed. Please try again." });
    } finally {
      setRevertingField(false);
    }
  };

  // Debounced user search for the assign section.
  useEffect(() => {
    const q = userQuery.trim();
    if (!q) { setUserResults([]); return undefined; }
    const h = setTimeout(async () => {
      setUserLoading(true);
      try {
        const res = await getRequest(`${url("user_search")}?q=${encodeURIComponent(q)}`);
        setUserResults((res && res.success && Array.isArray(res.data) ? res.data : (res && res.data && res.data.rows)) || []);
      } catch (_e) {
        setUserResults([]);
      } finally {
        setUserLoading(false);
      }
    }, 300);
    return () => clearTimeout(h);
  }, [userQuery, pageUrl]);

  const doAssign = async () => {
    const name = assignName.trim();
    if (!name) { setAssignMsg({ ok: false, text: "Enter a name to assign." }); return; }
    if (!selectedUser) { setAssignMsg({ ok: false, text: "Search and select a user first." }); return; }
    setAssigning(true);
    setAssignMsg(null);
    try {
      const res = await postRequest(url("assign_name"), {
        name,
        userId: selectedUser.id,
        email: selectedUser.email,
        reason: assignReason.trim(),
      });
      if (res && res.success) {
        const d = res.data || {};
        const who = (d.user && d.user.email) || selectedUser.email;
        setAssignMsg({
          ok: true,
          text: `Assigned ${d.vanityName || name} to ${who}${d.walletAddress ? ` → ${d.walletAddress}` : ""}.${d.wasReserved ? " (was a reserved name)" : ""}`,
        });
        setAssignName(""); setAssignReason(""); setSelectedUser(null); setUserQuery(""); setUserResults([]);
        loadNames(); // refresh assignment status so the table reflects the new assignment
      } else {
        setAssignMsg({ ok: false, text: (res && res.error) || "Assign failed." });
      }
    } catch (err) {
      console.error("Assign name failed:", err);
      setAssignMsg({ ok: false, text: err?.data?.error || err?.data?.message || "Assign failed. Please try again." });
    } finally {
      setAssigning(false);
    }
  };

  const sourceBadge = (text, bg, color, border) => (
    <span style={{ fontSize: "10px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.4px",
                   background: bg, color, border: `1px solid ${border}`, borderRadius: "10px", padding: "1px 7px", marginLeft: "6px" }}>
      {text}
    </span>
  );

  // Show the effective receiver address + where it comes from: an admin override
  // ("custom") or the env default address returned by the BE ("env default").
  const renderCurrent = (chain) => {
    const set = receivers[chain];
    const def = defaults[chain];
    if (set) {
      return <span><code style={{ fontFamily: "monospace", color: colors.primary }}>{set}</code>{sourceBadge("custom", "#d1ecf1", "#0c5460", "#bee5eb")}</span>;
    }
    if (def) {
      return <span><code style={{ fontFamily: "monospace", color: colors.gray700 }}>{def}</code>{sourceBadge("env default", "#e9ecef", "#495057", "#dee2e6")}</span>;
    }
    return <span style={{ color: colors.gray500, fontStyle: "italic" }}>using env default (not configured)</span>;
  };

  const renderPicker = (field) => {
    if (pickerFor !== field) return null;
    return (
      <div style={{ position: "absolute", zIndex: 20, left: 0, right: 0, marginTop: "6px", border: `1px solid ${colors.border}`, borderRadius: "8px", background: colors.white, boxShadow: "0 8px 24px rgba(0,0,0,0.14)" }}>
        <div style={{ padding: "8px" }}>
          <input autoFocus value={pickerQuery} onChange={(e) => setPickerQuery(e.target.value)}
                 placeholder="Search email or username…"
                 style={{ ...input, fontFamily: "sans-serif" }} />
        </div>
        <div style={{ maxHeight: "230px", overflowY: "auto", borderTop: `1px solid ${colors.gray100 || "#eee"}` }}>
          {pickerLoading && <div style={{ padding: "10px 12px", color: colors.gray500, fontSize: "13px" }}>Loading…</div>}
          {!pickerLoading && pickerRows.length === 0 && (
            <div style={{ padding: "10px 12px", color: colors.gray500, fontSize: "13px" }}>No matching accounts with a {field === "hedera" ? "Hedera" : "Polygon"} address.</div>
          )}
          {!pickerLoading && pickerRows.map((r, i) => (
            <div key={i} role="button" tabIndex={0} onClick={() => pickAddress(r.address)}
                 onKeyDown={(e) => { if (e.key === "Enter") pickAddress(r.address); }}
                 style={{ borderBottom: `1px solid ${colors.gray100 || "#f2f2f2"}`, padding: "8px 12px", cursor: "pointer" }}>
              <div style={{ fontSize: "13px", fontWeight: 600, color: colors.primary, lineHeight: 1.4 }}>{r.name || r.username || r.email || "-"}</div>
              <div style={{ fontSize: "11px", color: colors.gray600, lineHeight: 1.4 }}>{r.email}{r.username ? ` · @${r.username}` : ""}</div>
              <div style={{ fontSize: "11px", color: colors.gray500, fontFamily: "monospace", lineHeight: 1.4, wordBreak: "break-all" }}>{r.address}</div>
            </div>
          ))}
        </div>
        <div style={{ padding: "6px 8px", textAlign: "right", borderTop: `1px solid ${colors.gray100 || "#eee"}` }}>
          <button type="button" onClick={() => setPickerFor(null)}
                  style={{ background: "none", border: "none", color: "#666", cursor: "pointer", fontSize: "12px" }}>Close</button>
        </div>
      </div>
    );
  };

  return (
    <div style={{ paddingBottom: "24px", maxWidth: "760px" }}>
      <div style={{ ...pageHeaderStyles.container, marginBottom: "20px", borderRadius: "12px" }}>
        <h1 style={pageHeaderStyles.title}>Agent Wallet Setting</h1>
      </div>

      {/* ── Reserved names ── */}
      <div style={card}>
        <div style={{ fontSize: "15px", fontWeight: 700, color: colors.primary, marginBottom: "4px" }}>Reserved agent names</div>
        <div style={{ fontSize: "12px", color: colors.gray600, marginBottom: "14px" }}>
          Names here are blocked from public sale. Add or remove any name, including <code>zero</code>.
        </div>

        {names.length === 0 ? (
          <div style={{ color: colors.gray500, fontSize: "13px", marginBottom: "14px" }}>No extra reserved names.</div>
        ) : (
          <div style={{ marginBottom: "14px" }}>
            {names.length > 8 && (
              <input style={{ ...input, fontFamily: "sans-serif", marginBottom: "8px" }} value={nameFilter}
                     onChange={(e) => setNameFilter(e.target.value)} placeholder="Filter reserved names…" />
            )}
            <div style={{ maxHeight: "320px", overflowY: "auto", border: `1px solid ${colors.border}`, borderRadius: "8px" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
                <thead>
                  <tr>
                    {["Name", "Status", "Assigned to", ""].map((h, i) => (
                      <th key={i} style={{ textAlign: i === 3 ? "right" : "left", padding: "8px 12px", borderBottom: `2px solid ${colors.border}`, fontSize: "11px", textTransform: "uppercase", letterSpacing: "0.5px", color: colors.gray600, position: "sticky", top: 0, background: colors.white }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {names
                    .filter((n) => n.toLowerCase().includes(nameFilter.trim().toLowerCase()))
                    .map((n) => {
                      const a = assignments[n] || {};
                      const td = { padding: "8px 12px", borderBottom: `1px solid ${colors.gray100 || "#f2f2f2"}` };
                      return (
                        <tr key={n}>
                          <td style={td}><code style={{ fontFamily: "monospace", color: colors.primary }}>{n}</code></td>
                          <td style={td}>
                            {a.assigned
                              ? <span style={{ color: "#1e8449", fontWeight: 700 }}>● Assigned</span>
                              : <span style={{ color: colors.gray500 }}>○ Unassigned</span>}
                          </td>
                          <td style={{ ...td, color: colors.gray700 }}>
                            {a.assignedTo
                              ? `${a.assignedTo.email || ""}${a.assignedTo.username ? ` · @${a.assignedTo.username}` : ""}`.trim()
                              : "-"}
                          </td>
                          <td style={{ ...td, textAlign: "right" }}>
                            {a.assigned
                              ? <span title="Assigned names can't be removed here" style={{ color: colors.gray400 || "#bbb", fontSize: "11px", fontStyle: "italic" }}>locked</span>
                              : <button type="button" onClick={() => removeName(n)} title="Remove"
                                        style={{ background: "none", border: "none", cursor: "pointer", color: "#999", fontSize: "16px", lineHeight: 1, padding: 0 }}>×</button>}
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <div style={{ display: "flex", gap: "8px", marginBottom: "12px" }}>
          <input style={{ ...input, flex: 1 }} value={newName}
                 maxLength={40}
                 onChange={(e) => setNewName(e.target.value.replace(/\s+/g, "-"))}
                 onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addName(); } }}
                 placeholder="Add a name to reserve (e.g. banana)" />
          <button type="button" onClick={addName} style={btn(colors.accent || "#e58734", false)}>Add</button>
        </div>

        <button type="button" onClick={saveNames} disabled={savingNames} style={btn(colors.primary, savingNames)}>
          {savingNames ? "Saving…" : "Save reserved names"}
        </button>
        {namesMsg && <div style={notice(namesMsg.ok)}>{namesMsg.text}</div>}
      </div>

      {/* ── USDC receivers ── */}
      <div style={card}>
        <div style={{ fontSize: "15px", fontWeight: 700, color: colors.primary, marginBottom: "4px" }}>USDC receiver wallets</div>
        <div style={{ fontSize: "12px", color: colors.gray600, marginBottom: "14px" }}>
          Where Genesis USDC payments are received, per chain. <strong style={{ color: "#932419" }}>This controls where real funds land</strong>: double-check before saving. Leave a field blank to keep the current value.
        </div>

        <div style={{ background: colors.gray100 || "#f7f8fa", borderRadius: "8px", padding: "12px 14px", marginBottom: "16px", fontSize: "13px" }}>
          {["hedera", "polygon"].map((chain, idx) => (
            <div key={chain} style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap", marginBottom: idx === 0 ? "6px" : 0 }}>
              <span>Current, <strong>{chain === "hedera" ? "Hedera" : "Polygon"}</strong>: {renderCurrent(chain)}</span>
              {receivers[chain] && revertField !== chain && (
                <button type="button" onClick={() => { setRecvMsg(null); setRevertField(chain); }}
                        style={{ background: "none", border: "none", color: "#c0392b", cursor: "pointer", fontSize: "12px", textDecoration: "underline", padding: 0 }}>
                  Revert to env default
                </button>
              )}
              {revertField === chain && (
                <span style={{ display: "inline-flex", alignItems: "center", gap: "8px" }}>
                  <span style={{ color: "#9a3412", fontSize: "12px" }}>Clear {chain} back to env default?</span>
                  <button type="button" disabled={revertingField} onClick={submitRevert}
                          style={{ background: "#c0392b", color: "#fff", border: "none", borderRadius: "5px", padding: "4px 10px", fontSize: "12px", fontWeight: 600, cursor: revertingField ? "not-allowed" : "pointer", opacity: revertingField ? 0.6 : 1 }}>
                    {revertingField ? "…" : "Confirm revert"}
                  </button>
                  <button type="button" disabled={revertingField} onClick={() => setRevertField(null)}
                          style={{ background: "none", border: "none", color: "#666", cursor: "pointer", fontSize: "12px", padding: 0 }}>Cancel</button>
                </span>
              )}
            </div>
          ))}
        </div>

        <div style={{ display: "flex", gap: "14px", flexWrap: "wrap", marginBottom: "16px" }}>
          <div style={{ flex: "1 1 260px", position: "relative" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
              <label style={label}>Hedera receiver (0.0.X)</label>
              <button type="button" onClick={() => openPicker("hedera")}
                      style={{ background: "none", border: "none", color: colors.accent || "#e58734", cursor: "pointer", fontSize: "12px", textDecoration: "underline", padding: 0 }}>
                Choose from account
              </button>
            </div>
            <input style={{ ...input, borderColor: hederaErr ? "#dc3545" : "#ccc" }} value={hedera}
                   onChange={(e) => setHedera(e.target.value)} placeholder={receivers.hedera || "0.0.12345"} />
            {hederaErr && <div style={{ color: "#dc3545", fontSize: "11px", marginTop: "4px" }}>Must look like 0.0.12345</div>}
            {renderPicker("hedera")}
          </div>
          <div style={{ flex: "1 1 260px", position: "relative" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
              <label style={label}>Polygon receiver (0x…40 hex)</label>
              <button type="button" onClick={() => openPicker("polygon")}
                      style={{ background: "none", border: "none", color: colors.accent || "#e58734", cursor: "pointer", fontSize: "12px", textDecoration: "underline", padding: 0 }}>
                Choose from account
              </button>
            </div>
            <input style={{ ...input, borderColor: polygonErr ? "#dc3545" : "#ccc" }} value={polygon}
                   onChange={(e) => setPolygon(e.target.value)} placeholder={receivers.polygon || "0x…"} />
            {polygonErr && <div style={{ color: "#dc3545", fontSize: "11px", marginTop: "4px" }}>Must be 0x + 40 hex chars</div>}
            {renderPicker("polygon")}
          </div>
        </div>

        {!confirmOpen ? (
          <button type="button"
                  onClick={() => { setRecvMsg(null); setConfirmOpen(true); }}
                  disabled={nothingToSave || hederaErr || polygonErr}
                  style={btn(colors.primary, nothingToSave || hederaErr || polygonErr)}>
            Save receiver wallet(s)
          </button>
        ) : (
          <div style={{ background: "#fff7ed", border: "1px solid #fed7aa", borderRadius: "8px", padding: "14px 16px" }}>
            <div style={{ fontWeight: 700, color: "#9a3412", marginBottom: "8px" }}>Confirm, funds will be sent to:</div>
            {hedera.trim() && <div style={{ fontSize: "13px", marginBottom: "4px" }}>Hedera → <code>{hedera.trim()}</code></div>}
            {polygon.trim() && <div style={{ fontSize: "13px", marginBottom: "4px" }}>Polygon → <code>{polygon.trim()}</code></div>}
            <div style={{ display: "flex", gap: "10px", marginTop: "12px" }}>
              <button type="button" onClick={() => setConfirmOpen(false)} disabled={savingRecv}
                      style={{ display: "inline-flex", alignItems: "center", minHeight: "38px", background: "#e2e3e5", color: "#333", border: "none", borderRadius: "6px", padding: "9px 16px", fontSize: "13px", fontWeight: 600, cursor: "pointer" }}>
                Cancel
              </button>
              <button type="button" onClick={saveReceivers} disabled={savingRecv} style={btn("#c0392b", savingRecv)}>
                {savingRecv ? "Saving…" : "Confirm & save"}
              </button>
            </div>
          </div>
        )}
        {recvMsg && <div style={notice(recvMsg.ok)}>{recvMsg.text}</div>}
      </div>

      {/* ── Assign reserved name to a user (no payment) ── */}
      <div style={card}>
        <div style={{ fontSize: "15px", fontWeight: 700, color: colors.primary, marginBottom: "4px" }}>Assign reserved name to a user (no payment)</div>
        <div style={{ fontSize: "12px", color: colors.gray600, marginBottom: "14px" }}>
          Hand a reserved or available agent name to a user&apos;s wallet for free, no checkout. Enter the bare name (e.g. <code>zero</code>); it becomes <code>Agent-zero</code>.
        </div>

        <label style={label}>User</label>
        {selectedUser ? (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "10px", border: `1px solid ${colors.border}`, borderRadius: "6px", padding: "8px 12px", marginBottom: "14px" }}>
            <div>
              <div style={{ fontWeight: 600, color: colors.primary, fontSize: "13px" }}>{selectedUser.fullName || selectedUser.username || selectedUser.email}</div>
              <div style={{ fontSize: "11px", color: colors.gray600 }}>{selectedUser.email}{selectedUser.username ? ` · @${selectedUser.username}` : ""}</div>
            </div>
            <button type="button" onClick={() => setSelectedUser(null)}
                    style={{ background: "none", border: "none", color: "#c0392b", cursor: "pointer", fontSize: "12px" }}>Change</button>
          </div>
        ) : (
          <div style={{ position: "relative", marginBottom: "14px" }}>
            <input style={{ ...input, fontFamily: "sans-serif" }} value={userQuery}
                   onChange={(e) => setUserQuery(e.target.value)} placeholder="Search by email, username, or name…" />
            {userQuery.trim() && (userLoading || userResults.length >= 0) && (
              <div style={{ position: "absolute", zIndex: 20, left: 0, right: 0, marginTop: "4px", border: `1px solid ${colors.border}`, borderRadius: "8px", background: colors.white, boxShadow: "0 8px 24px rgba(0,0,0,0.14)", maxHeight: "240px", overflowY: "auto" }}>
                {userLoading && <div style={{ padding: "10px 12px", color: colors.gray500, fontSize: "13px" }}>Searching…</div>}
                {!userLoading && userResults.length === 0 && <div style={{ padding: "10px 12px", color: colors.gray500, fontSize: "13px" }}>No matching users.</div>}
                {!userLoading && userResults.map((u) => (
                  <div key={u.id} role="button" tabIndex={0}
                       onClick={() => { setSelectedUser(u); setUserQuery(""); setUserResults([]); }}
                       onKeyDown={(e) => { if (e.key === "Enter") { setSelectedUser(u); setUserQuery(""); setUserResults([]); } }}
                       style={{ borderBottom: `1px solid ${colors.gray100 || "#f2f2f2"}`, padding: "8px 12px", cursor: "pointer" }}>
                    <div style={{ fontSize: "13px", fontWeight: 600, color: colors.primary, lineHeight: 1.4 }}>{u.fullName || u.username || u.email}</div>
                    <div style={{ fontSize: "11px", color: colors.gray600, lineHeight: 1.4 }}>{u.email}{u.username ? ` · @${u.username}` : ""}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <div style={{ display: "flex", gap: "14px", flexWrap: "wrap", marginBottom: "14px" }}>
          <div style={{ flex: "1 1 220px" }}>
            <label style={label}>Name to assign</label>
            <input list="reserved-names-list" style={{ ...input, fontFamily: "sans-serif" }} value={assignName}
                   maxLength={40}
                   onChange={(e) => setAssignName(e.target.value.replace(/\s+/g, "-"))}
                   placeholder="e.g. zero" />
            <datalist id="reserved-names-list">
              {names.map((n) => <option key={n} value={n} />)}
            </datalist>
            {assignConflict && (
              <div style={{ color: "#dc3545", fontSize: "11px", marginTop: "4px" }}>
                Already assigned{assignTarget.assignedTo
                  ? ` to ${assignTarget.assignedTo.email || `@${assignTarget.assignedTo.username}`}`
                  : ""}, pick another name.
              </div>
            )}
          </div>
          <div style={{ flex: "1 1 220px" }}>
            <label style={label}>Reason (optional)</label>
            <input style={{ ...input, fontFamily: "sans-serif" }} value={assignReason}
                   onChange={(e) => setAssignReason(e.target.value)} placeholder="e.g. compensation / VIP" />
          </div>
        </div>

        <button type="button" onClick={doAssign} disabled={assigning || !assignName.trim() || !selectedUser || assignConflict}
                style={btn(colors.primary, assigning || !assignName.trim() || !selectedUser || assignConflict)}>
          {assigning ? "Assigning…" : "Assign to user"}
        </button>
        {assignMsg && <div style={notice(assignMsg.ok)}>{assignMsg.text}</div>}
      </div>
    </div>
  );
};

export default GenesisSettings;
